import { format, isDate } from 'date-fns'

const kinds = {
  ident: 1,
  equals: 2,
  number: 3,
  and: 4,
  or: 5,
  eof: 6,
  unknown: 7,
  notEquals: 8,
  not: 9,
  lParen: 10,
  rParen: 11,
  comma: 12,
  call: 13,
  string: 14,
  greaterThan: 15,
  lessThan: 16,
  greaterThanOrEqual: 17,
  is: 18,
  null: 19,
  like: 20,
  in: 21,
  param: 22,
  asc: 23,
  desc: 24,
  collate: 25,
  nocase: 26,
}

const rules = {
  [kinds.ident]: { nud: parseIdent },
  [kinds.number]: { nud: parseNumber },
  [kinds.not]: { nud: parseUnary },
  [kinds.string]: { nud: parseString },
  [kinds.null]: { nud: parseKeywordLiteral },
  [kinds.param]: { nud: parseParam },
  [kinds.or]: { led: parseBinary, power: 1 },
  [kinds.asc]: { led: parseDirection, power: 1 },
  [kinds.desc]: { led: parseDirection, power: 1 },
  [kinds.and]: { led: parseBinary, power: 2 },
  [kinds.equals]: { led: parseBinary, power: 3 },
  [kinds.notEquals]: { led: parseBinary, power: 3 },
  [kinds.greaterThan]: { led: parseBinary, power: 3 },
  [kinds.lessThan]: { led: parseBinary, power: 3 },
  [kinds.greaterThanOrEqual]: { led: parseBinary, power: 3 },
  [kinds.lessThanOrEqaul]: { led: parseBinary, power: 3 },
  [kinds.is]: { led: parseBinary, power: 3 },
  [kinds.like]: { led: parseBinary, power: 3 },
  [kinds.in]: { led: parseBinary, power: 3 },
  [kinds.lParen]: { nud: parseGroup, led: parseCall, power: 5 },
  [kinds.collate]: { led: parseBinary, power: 4 },
  [kinds.nocase]: { nud: parseKeywordLiteral },
}

let tokens = []
let chars = []

function consumeToken () {
  tokens.shift()
}

function parseDirection (lhs) {
  const current = currentToken()

  consumeToken()

  if (currentToken().kind === kinds.comma) {
    consumeToken()
  }

  return {
    token: current,
    lhs,
    toString () {
      return `(${current.lexeme} ${lhs.toString()})`
    },
  }
}

function parseKeywordLiteral () {
  const current = currentToken()
  consumeToken()

  return {
    value: current.lexeme,
    token: current,
    toString () {
      return current.lexeme
    },
  }
}

function parseParam () {
  const current = currentToken()
  consumeToken()

  return {
    token: current,
    toString () {
      return current.lexeme
    },
  }
}

function parseString () {
  const current = currentToken()
  consumeToken()

  return {
    value: current.lexeme,
    token: current,
    toString () {
      return current.lexeme
    },
  }
}

function parseGroup () {
  consumeToken()

  const expr = parseExpr(0)

  consumeToken()

  return expr
}

function parseArgs () {
  consumeToken()

  const args = []
  while (currentToken().kind !== kinds.rParen) {
    args.push(parseExpr(0))

    if (currentToken().kind === kinds.comma) {
      consumeToken()
    }
  }

  consumeToken()

  return args
}

function parseCall (lhs) {
  const args = parseArgs()

  return {
    token: createToken(kinds.call, lhs.toString()),
    name: lhs.name,
    args,
    toString () {
      return `(fn ${lhs.toString()}\n${indent(args.toString())})`
    },
  }
}

function parseUnary () {
  const current = currentToken()
  consumeToken()

  const rhs = parseExpr(0)

  return {
    token: current,
    rhs,
    toString () {
      return `(${current.lexeme}\n${indent(rhs.toString())})`
    },
  }
}

function parseIdent () {
  const current = currentToken()
  consumeToken()

  return {
    name: current.lexeme,
    token: current,
    toString () {
      return `(ident ${current.lexeme})`
    },
  }
}

function parseNumber () {
  const current = currentToken()
  consumeToken()

  return {
    value: Number(current.lexeme),
    token: current,
    toString () {
      return current.lexeme
    },
  }
}

function parseBinary (lhs) {
  const op = currentToken()

  const next = peekToken()
  let rhs = null

  if (op.kind === kinds.is) {
    if (next.kind !== kinds.null && next.kind !== kinds.not) {
      throw new Error('expecting null or not')
    }
  }

  if (op.kind === kinds.collate) {
    if (next.kind !== kinds.nocase) {
      throw new Error('expecting nocase')
    }
  }

  const rbp = (rules[op.kind] && rules[op.kind].power) || 0
  consumeToken()

  if (op.kind === kinds.in) {
    rhs = parseArgs()
  } else {
    rhs = parseExpr(rbp)
  }

  return {
    lhs,
    op,
    rhs,
    token: op,
    toString () {
      return `(${op.lexeme}\n${indent(lhs.toString())}\n${indent(rhs.toString())})`
    },
  }
}

function indent (s) {
  const indented = []

  const lines = s.split('\n')
  for (const l of lines) {
    indented.push('  ' + l)
  }

  return indented.join('\n')
}

function parseExpr (rbp) {
  const nud = rules[currentToken().kind] && rules[currentToken().kind].nud
  if (!nud) {
    throw new Error('Could not parse token ' + JSON.stringify(currentToken()))
  }

  let lhs = nud()
  let lbp = (rules[currentToken().kind] && rules[currentToken().kind].power) || 0
  while (rbp < lbp) {
    const led = rules[currentToken().kind] && rules[currentToken().kind].led
    if (!led) {
      return lhs
    }

    lhs = led(lhs)
    lbp = (rules[currentToken().kind] && rules[currentToken().kind].power) || 0
  }

  return lhs
}

function currentToken () {
  return tokens[0] || {}
}

function peekToken () {
  return tokens[1] || {}
}

function currentChar () {
  return chars[0] || {}
}

function nextToken () {
  readWhile(isWhitespace)

  const c = currentChar()
  switch (true) {
    case isIdent(c): {
      let lexeme = readWhile(isIdent)
      if (currentChar() === '.') {
        lexeme += readSingle()
        lexeme += readWhile(isIdent)
      }

      switch (lexeme.toLowerCase()) {
        case 'and': return createToken(kinds.and, lexeme)
        case 'or': return createToken(kinds.or, lexeme)
        case 'not': return createToken(kinds.not, lexeme)
        case 'is': return createToken(kinds.is, lexeme)
        case 'null': return createToken(kinds.null, lexeme)
        case 'like': return createToken(kinds.like, lexeme)
        case 'in': return createToken(kinds.in, lexeme)
        case 'asc': return createToken(kinds.asc, lexeme)
        case 'desc': return createToken(kinds.desc, lexeme)
        case 'nocase': return createToken(kinds.nocase, lexeme)
        case 'collate': return createToken(kinds.collate, lexeme)
      }

      return createToken(kinds.ident, lexeme)
    }
    case isPunc(c): {
      let lexeme = readSingle()

      let kind = kinds.unknown
      switch (lexeme) {
        case '=': kind = kinds.equals; break
        case '(': kind = kinds.lParen; break
        case ')': kind = kinds.rParen; break
        case ',': kind = kinds.comma; break
        case '?': kind = kinds.param; break
        case '>':
          if (currentChar() === '=') {
            lexeme += readSingle()
            kind = kinds.greaterThanOrEqual
          } else {
            kind = kinds.greaterThan
          }

          break
        case '<':
          if (currentChar() === '=') {
            lexeme += readSingle()
            kind = kinds.lessThanOrEqaul
          } else {
            kind = kinds.lessThan
          }

          break
        case '!':
          if (currentChar() === '=') {
            lexeme += readSingle()
            kind = kinds.notEquals
          }
      }

      return createToken(kind, lexeme)
    }
    case isDigit(c): {
      let lexeme = readWhile(isDigit)
      if (currentChar() === '.') {
        lexeme += readSingle()
        lexeme += readWhile(isDigit)
      }

      return createToken(kinds.number, lexeme)
    }
    case c === `'`: {
      readSingle()

      const lexeme = readWhile(inString)

      readSingle()

      return createToken(kinds.string, lexeme)
    }
  }

  return createToken(kinds.eof, 'eof')
}

function tokenise (str) {
  tokens = []
  chars = str.split('')

  while (chars.length) {
    tokens.push(nextToken())
  }

  tokens.push(createToken(kinds.eof, 'eof'))
}

function createToken (kind, lexeme) {
  return {
    kind,
    lexeme,
  }
}

function inString (char) {
  return char !== `'`
}

function isDigit (char) {
  return char >= '0' && char <= '9'
}

function isPunc (char) {
  return ['=', '!', '(', ')', ',', '>', '<', '?'].includes(char)
}

function isIdent (char) {
  return isAlpha(char) || char === '_'
}

function isAlpha (char) {
  return (char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z')
}

function readSingle () {
  return chars.shift() || ''
}

function readWhile (p) {
  let c = chars[0] || ''
  let s = ''

  while (p(c)) {
    s += c

    chars.shift()
    c = chars[0] || ''
  }

  return s
}

function isWhitespace (char) {
  return char === ' ' || char === '\t' || char === '\n'
}

function parse (str, type) {
  tokenise(str)

  let token = currentToken()
  const exprs = []
  while (token.kind !== kinds.eof) {
    exprs.push(parseExpr(0))

    if (type === 'order' && currentToken().kind === kinds.comma) {
      consumeToken()
    }

    token = currentToken()
  }

  if (type === 'where') {
    return exprs.pop()
  }

  return exprs
}

const funcs = {
  substr (str, start, length) {
    return str.substr(start, length)
  },
  trim (str) {
    return str.trim()
  },
  rtrim (str) {
    return str.trimRight()
  },
  replace (str, inStr, replace) {
    return str.replace(inStr, replace)
  },
  instr (str, val) {
    return str.indexOf(val)
  },
  json_extract (json, path) {
    if (typeof json === 'string') {
      json = JSON.parse(json)
    }

    const idents = path.split('.')
    const dollar = idents.shift()
    if (dollar !== '$') {
      throw new Error('json_extract expected $')
    }

    for (const ident of idents) {
      if (typeof json[ident] === 'undefined') {
        return null
      }

      json = json[ident]
    }

    if (typeof json !== 'string') {
      json = JSON.stringify(json)
    }

    return json
  },
  json_valid (json) {
    if (typeof json !== 'string') {
      json = JSON.stringify(json)
    }

    try {
      JSON.parse(json)
    } catch (err) {
      return false
    }

    return json
  },
  date (date) {
    const dateFormat = 'yyyy-MM-dd'
    if (!date || !isDate(date)) {
      return
    }

    if (date === 'now') {
      return format(new Date(), dateFormat)
    }

    return format(new Date(date), 'yyyy-MM-dd')
  },
}

let ptr = 0

function executeLiteral (row, node) {
  switch (node.token.kind) {
    case kinds.number: {
      return node.value
    }
    case kinds.string: {
      return node.value
    }
    case kinds.null: {
      return null
    }
    case kinds.ident: {
      const col = node.name.split('.').pop()

      return row[col]
    }
    case kinds.nocase: {
      return node.value
    }
  }

  throw new Error('unexpected kind ' + JSON.stringify(node))
}

function executeWhere (row, node, whereArgs) {
  switch (node.token.kind) {
    case kinds.nocase:
    case kinds.number:
    case kinds.string:
    case kinds.null:
    case kinds.ident:
      return executeLiteral(row, node)
    case kinds.and: {
      const lhs = executeWhere(row, node.lhs, whereArgs)
      if (!lhs) {
        return lhs
      }

      const rhs = executeWhere(row, node.rhs, whereArgs)

      return lhs && rhs
    }
    case kinds.or: {
      const lhs = executeWhere(row, node.lhs, whereArgs)
      if (lhs) {
        return lhs
      }

      const rhs = executeWhere(row, node.rhs, whereArgs)

      return lhs || rhs
    }
    case kinds.is: {
      const lhs = executeWhere(row, node.lhs, whereArgs)
      const rhs = executeWhere(row, node.rhs, whereArgs)

      switch (true) {
        case rhs === null: return lhs === null
        case rhs: return lhs !== null
      }
    } break
    case kinds.equals: {
      const lhs = executeWhere(row, node.lhs, whereArgs)
      const rhs = executeWhere(row, node.rhs, whereArgs)

      return lhs === rhs
    }
    case kinds.notEquals: {
      const lhs = executeWhere(row, node.lhs, whereArgs)
      const rhs = executeWhere(row, node.rhs, whereArgs)

      return lhs !== rhs
    }
    case kinds.greaterThan: {
      const lhs = executeWhere(row, node.lhs, whereArgs)
      const rhs = executeWhere(row, node.rhs, whereArgs)

      return lhs > rhs
    }
    case kinds.lessThan: {
      const lhs = executeWhere(row, node.lhs, whereArgs)
      const rhs = executeWhere(row, node.rhs, whereArgs)

      return lhs < rhs
    }
    case kinds.greaterThanOrEqual: {
      const lhs = executeWhere(row, node.lhs, whereArgs)
      const rhs = executeWhere(row, node.rhs, whereArgs)

      return lhs >= rhs
    }
    case kinds.lessThanOrEqaul: {
      const lhs = executeWhere(row, node.lhs, whereArgs)
      const rhs = executeWhere(row, node.rhs, whereArgs)

      return lhs <= rhs
    }
    case kinds.not: {
      const rhs = executeWhere(row, node.rhs, whereArgs)

      return !rhs
    }
    case kinds.call: {
      const name = node.name.toLowerCase()

      const func = funcs[name]
      const args = node.args.map(a => executeWhere(row, a, whereArgs))

      return func(...args)
    }
    case kinds.like: {
      const val = executeWhere(row, node.lhs, whereArgs)
      const sqlPattern = executeWhere(row, node.rhs, whereArgs)

      const rePattern = `^${escapeRegExp(String(sqlPattern)).replace(/%/g, '[^]*')}$`
      const r = new RegExp(rePattern)

      return r.test(String(val))
    }
    case kinds.in: {
      const lhs = executeWhere(row, node.lhs, whereArgs)
      const args = node.rhs.map(a => executeWhere(row, a, whereArgs))

      return args.includes(lhs)
    }
    case kinds.param: {
      const arg = whereArgs[ptr]

      ptr++

      return arg
    }
    case kinds.collate: {
      const lhs = executeWhere(row, node.lhs, whereArgs)
      const rhs = executeWhere(row, node.rhs, whereArgs)

      if (rhs.toLowerCase() === 'nocase') {
        return lhs.toLowerCase()
      }

      return rhs
    }
  }

  throw new Error('unexpected kind ' + JSON.stringify(node))
}

function executeOrder (a, b, node, orderArgs) {
  switch (node.token.kind) {
    case kinds.nocase:
    case kinds.number:
    case kinds.string:
    case kinds.null:
      return executeLiteral(node)
    case kinds.ident: {
      const col = node.name.split('.').pop()

      return String(a[col]).localeCompare(String(b[col]))
    }
    case kinds.asc: {
      return executeOrder(a, b, node.lhs, orderArgs)
    }
    case kinds.desc: {
      return executeOrder(a, b, node.lhs, orderArgs) * -1
    }
    case kinds.call: {
      const name = node.name.toLowerCase()

      const func = funcs[name]
      const curPtr = ptr
      const aArgs = node.args.map(node => {
        if (node.token.kind === kinds.param) {
          return executeOrder(a, b, node, orderArgs)
        }

        return executeLiteral(a, node)
      })
      ptr = curPtr
      const bArgs = node.args.map(node => {
        if (node.token.kind === kinds.param) {
          return executeOrder(a, b, node, orderArgs)
        }

        return executeLiteral(b, node)
      })

      const aRes = func(...aArgs)
      const bRes = func(...bArgs)

      return String(aRes || '').localeCompare(String(bRes || ''))
    }
    case kinds.param: {
      const arg = orderArgs[ptr]

      ptr++

      return arg
    }
    case kinds.collate: {
      const col = node.lhs.name.split('.').pop()

      if (node.rhs.value.toLowerCase() === 'nocase') {
        return String(a[col].toLowerCase() || '').localeCompare(String(a[col].toLowerCase() || ''))
      }
    }
  }
}

function escapeRegExp (string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

function createFilter (ast, whereArgs) {
  return row => {
    ptr = 0

    return executeWhere(row, ast, whereArgs)
  }
}

function createSort (ast, orderArgs) {
  return (a, b) => {
    ptr = 0

    for (const node of ast) {
      const res = executeOrder(a, b, node, orderArgs)
      if (res) {
        return res
      }
    }

    return 0
  }
}

export function filter (where, args) {
  const whereAst = parse(where, 'where')

  return createFilter(whereAst, args)
}

export function order (order, args) {
  const orderAst = parse(order, 'order')

  return createSort(orderAst, args)
}
